import { usePathname, useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';

import useProductAnalytics from '@/analytics/hooks/useProductAnalytics';
import type { PayloadItems } from '@/analytics/types';
import {
  DEFAULT_REFRESH_CART_VALUES,
  useCartContext,
} from '@/app/contexts/CartProvider';
import MembershipSelectionEnum from '@/app/enums/MembershipSelectionEnum';
import WarrantySelectionEnum from '@/app/enums/WarrantySelectionEnum';
import { ProductInventoryStatus } from '@/app/types/ProductInventoryStatus';
import logToDatadog from '@/logger/logToDatadog';
import { addToCart } from '@/queries/AddToCart';
import { changeWarranty } from '@/queries/ChangeWarranty';
import { removeFromCart } from '@/queries/RemoveFromCart';
import { type ListProduct } from '@/queries/utils/mapProductFragmentToListProduct';
import { removeFromSessionStorage } from '@/utils/sessionStorage';

import type { Order } from '../_types/order';

export interface AddOrderToCartProps {
  order: Order;
  product: ListProduct | undefined;
  productsData: ListProduct[] | undefined;
  sessionStorageKey: string;
  setIsAddingToCart: (isAdding: boolean) => void;
  yearlyMembership?: ListProduct | undefined;
}

const useAddOrderToCart = ({
  order,
  product,
  productsData,
  sessionStorageKey,
  setIsAddingToCart,
  yearlyMembership,
}: AddOrderToCartProps) => {
  const { refreshCart } = useCartContext();
  const cart =
    refreshCart?.cart?.refreshCart.cart ??
    DEFAULT_REFRESH_CART_VALUES.cart!.refreshCart.cart!;
  const ringCount = cart.ringCount!;
  const { sendProductAddedEvent } = useProductAnalytics();
  const locale = useLocale();
  const path = usePathname();
  const router = useRouter();

  const membershipSelection = order.membership.value as MembershipSelectionEnum;
  const membershipSku = order.membership.sku;
  const productSku = order.product.sku;
  const warrantySelection = order.warranty.value as WarrantySelectionEnum;
  const warrantySku = order.warranty.sku;
  const stockStatus = order.product.stockStatus;
  const yearlyMembershipSku = yearlyMembership?.variants[0]?.sku;
  const productsDataBySku: Record<string, ListProduct> | undefined =
    productsData?.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.variants[0]!.skuCode]: cur,
      }),
      {},
    );
  const membershipProduct = productsDataBySku?.[membershipSku];
  const warrantyProduct = productsDataBySku?.[warrantySku];

  const handleAddToCart = async () => {
    const onSuccess = async () => {
      removeFromSessionStorage(sessionStorageKey);
      // Create array of all ProductAdded event payloads to send simultaneously
      const productAddedEventPayloads: PayloadItems[] = [
        {
          path,
          product,
          stockStatus,
        },
      ];

      // Form premium membership ProductAdded analytics payload
      if (
        membershipSelection === MembershipSelectionEnum.OneYear &&
        membershipProduct
      ) {
        productAddedEventPayloads.push({
          path,
          product: { ...membershipProduct, title: 'one-year-membership' },
          stockStatus: ProductInventoryStatus.inStock,
        });
      }

      // Form extended warranty ProductAdded analytics payload
      if (
        warrantySelection !== WarrantySelectionEnum.OneYear &&
        warrantyProduct
      ) {
        productAddedEventPayloads.push({
          path,
          product: {
            ...warrantyProduct,
            title: `${warrantySelection}-extended-warranty`,
          },
          stockStatus: ProductInventoryStatus.inStock,
        });
      }

      // Send all ProductAdded analytics events
      await Promise.all(
        productAddedEventPayloads.map((payload) =>
          sendProductAddedEvent(payload),
        ),
      );

      // Preserve all query parameters except currency when navigating to cart
      const currentUrl = new URL(window.location.href);
      const searchParams = new URLSearchParams(currentUrl.search);
      searchParams.delete('currency');
      const queryString = searchParams.toString();
      const cartUrl = queryString
        ? `/${locale}/cart?${queryString}`
        : `/${locale}/cart`;

      router.push(cartUrl);
    };
    // disable the add to cart button while the request is in progress
    setIsAddingToCart(true);
    try {
      await addToCart(productSku, warrantySku);
      if (membershipSelection === MembershipSelectionEnum.OneYear) {
        await addToCart(membershipSku, warrantySku);
      } else if (yearlyMembershipSku) {
        await removeFromCart(yearlyMembershipSku, warrantySku);
      }
      if (ringCount > 0) {
        try {
          await changeWarranty(warrantySku);
          await onSuccess();
        } catch (error) {
          logToDatadog({
            data: { warrantySku, ringCount },
            error,
            message: 'cart 2.0 changeWarranty failed after addToCart',
            module: 'cart',
          });
        }
      } else {
        await onSuccess();
      }
    } catch (error) {
      logToDatadog({
        error,
        message: 'Error thrown during addToCart (cart 2.0)',
        module: 'cart',
      });
    }
  };
  return { onAddToCart: handleAddToCart };
};

export default useAddOrderToCart;
